
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import basicDemo from './demo/basic.twig';
import data from './demo/data.json';
const basicTableCodeExample = (tableClass) => {
  const withArgs = tableClass ? ` with { table_class: '${tableClass}' }` : '';
  return `{% embed '@cloudfour/components/table/table.twig'${withArgs} only %}
  {% block caption %}
    {# Optionally add a caption string #}
    Comparing popular Cloud Four projects on GitHub.
  {% endblock %}
  {% block header %}
    {# Optionally add a single \`<tr>\` element #}
    <tr>...</tr>
  {% endblock %}
  {% block body %}
    {# Add \`<tr>\` elements #}
    <tr>...</tr>
    <tr>...</tr>
  {% endblock %}
  {% block footer %}
    {# Optionally add a single \`<tr>\` element #}
    <tr>...</tr>
  {% endblock %}
{% endembed %}`;
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Table" args={{
      hasHeader: true,
      hasFooter: true,
      hasCaption: true,
      isRuled: false,
      isStriped: false,
      numericData: false
    }} argTypes={{
      hasHeader: {
        name: 'Include Header',
        type: {
          name: 'boolean'
        }
      },
      hasFooter: {
        name: 'Include Footer',
        type: {
          name: 'boolean'
        }
      },
      hasCaption: {
        name: 'Include Caption',
        type: {
          name: 'boolean'
        }
      },
      isRuled: {
        name: 'Add `c-tabled--ruled` modifier to the table',
        type: {
          name: 'boolean'
        }
      },
      isStriped: {
        name: 'Add `c-tabled--striped` modifier to the table',
        type: {
          name: 'boolean'
        }
      },
      numericData: {
        name: 'Add `c-table--numeric` modifier to the table',
        type: {
          name: 'boolean'
        }
      }
    }} mdxType="Meta" />
    <h1>{`Table`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`c-table`}</inlineCode>{` class applies default styling to HTML `}<inlineCode parentName="p">{`table`}</inlineCode>{` elements.
A table can optionally have a header, a footer, and a caption.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Basic" parameters={{
        docs: {
          source: {
            code: basicTableCodeExample()
          }
        }
      }} mdxType="Story">
    {args => basicDemo({
          ...args,
          tableData: data
        })}
  </Story>
    </Canvas>
    <h2>{`Ruled Table`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`c-table--ruled`}</inlineCode>{` modifier adds horizontal rules in between table rows.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Ruled" args={{
        isRuled: true
      }} parameters={{
        docs: {
          source: {
            code: basicTableCodeExample('c-table--ruled')
          }
        }
      }} mdxType="Story">
    {args => basicDemo({
          ...args,
          tableData: data
        })}
  </Story>
    </Canvas>
    <h2>{`Striped Table`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`c-table--striped`}</inlineCode>{` modifier adds alternating background colors to table rows.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Striped" args={{
        isStriped: true
      }} parameters={{
        docs: {
          source: {
            code: basicTableCodeExample('c-table--striped')
          }
        }
      }} mdxType="Story">
    {args => basicDemo({
          ...args,
          tableData: data
        })}
  </Story>
    </Canvas>
    <h2>{`Numeric Data`}</h2>
    <p>{`Tables with lots of numeric data use a couple of modifications to make them
easier to read and understand.`}</p>
    <p>{`Numeric cells are right-aligned and use tabular figures to make it easier to
compare cells across a column.`}</p>
    <p>{`These changes can be achieved by adding `}<inlineCode parentName="p">{`c-table--numeric`}</inlineCode>{` to the table.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Numeric Data" args={{
        numericData: true
      }} parameters={{
        docs: {
          source: {
            code: basicTableCodeExample('c-table--numeric')
          }
        }
      }} mdxType="Story">
    {args => basicDemo({
          ...args,
          tableData: data
        })}
  </Story>
    </Canvas>
    <h2>{`Template Properties`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`table_class`}</inlineCode>{` (string): Append a class to the root `}<inlineCode parentName="li">{`<table>`}</inlineCode>{` element.`}</li>
    </ul>
    <h2>{`Template Blocks`}</h2>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`caption`}</inlineCode>{` (optional): Block to add a table caption string.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`header`}</inlineCode>{` (optional): Block to add a `}<inlineCode parentName="li">{`<tr>`}</inlineCode>{` for the table header.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`body`}</inlineCode>{`: Block to add table body `}<inlineCode parentName="li">{`<tr>`}</inlineCode>{` elements.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`footer`}</inlineCode>{` (optional): Block to add a `}<inlineCode parentName="li">{`<tr>`}</inlineCode>{` for the table footer.`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

export const basic = (args => basicDemo({
  ...args,
  tableData: data
}));
basic.storyName = 'Basic';
basic.parameters = { storySource: { source: 'args => basicDemo({\n  ...args,\n  tableData: data\n})' }, ...{
  docs: {
    source: {
      code: basicTableCodeExample()
    }
  }
} };

export const ruled = (args => basicDemo({
  ...args,
  tableData: data
}));
ruled.storyName = 'Ruled';
ruled.args = {
  isRuled: true
};
ruled.parameters = { storySource: { source: 'args => basicDemo({\n  ...args,\n  tableData: data\n})' }, ...{
  docs: {
    source: {
      code: basicTableCodeExample('c-table--ruled')
    }
  }
} };

export const striped = (args => basicDemo({
  ...args,
  tableData: data
}));
striped.storyName = 'Striped';
striped.args = {
  isStriped: true
};
striped.parameters = { storySource: { source: 'args => basicDemo({\n  ...args,\n  tableData: data\n})' }, ...{
  docs: {
    source: {
      code: basicTableCodeExample('c-table--striped')
    }
  }
} };

export const numericData = (args => basicDemo({
  ...args,
  tableData: data
}));
numericData.storyName = 'Numeric Data';
numericData.args = {
  numericData: true
};
numericData.parameters = { storySource: { source: 'args => basicDemo({\n  ...args,\n  tableData: data\n})' }, ...{
  docs: {
    source: {
      code: basicTableCodeExample('c-table--numeric')
    }
  }
} };

const componentMeta = { title: 'Components/Table', args: {
  hasHeader: true,
  hasFooter: true,
  hasCaption: true,
  isRuled: false,
  isStriped: false,
  numericData: false
}, argTypes: {
  hasHeader: {
    name: 'Include Header',
    type: {
      name: 'boolean'
    }
  },
  hasFooter: {
    name: 'Include Footer',
    type: {
      name: 'boolean'
    }
  },
  hasCaption: {
    name: 'Include Caption',
    type: {
      name: 'boolean'
    }
  },
  isRuled: {
    name: 'Add `c-tabled--ruled` modifier to the table',
    type: {
      name: 'boolean'
    }
  },
  isStriped: {
    name: 'Add `c-tabled--striped` modifier to the table',
    type: {
      name: 'boolean'
    }
  },
  numericData: {
    name: 'Add `c-table--numeric` modifier to the table',
    type: {
      name: 'boolean'
    }
  }
}, includeStories: ["basic","ruled","striped","numericData"],  };

const mdxStoryNameToKey = {"Basic":"basic","Ruled":"ruled","Striped":"striped","Numeric Data":"numericData"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;